/** Wording for calendar event thread lines (#49). Shared so the API and tests agree. */
import { DateTime } from 'luxon';
import { formatEventWhen, type Rsvp } from './calendar';

const ANSWER: Record<Exclude<Rsvp, 'pending'>, string> = { accepted: 'Going', tentative: 'Maybe', declined: 'Not going' };

/**
 * "Tom Evans is attending", "Emma Clarke changed to Maybe", "Gary Hughes can't make it". `before` is the answer
 * before this change (or before a burst of changes that was collapsed into one line).
 */
export function rsvpLineText(name: string, rsvp: Exclude<Rsvp, 'pending'>, before: Rsvp | null | undefined): string {
  if (rsvp === 'declined') return `${name} can't make it`;
  const answered = before === 'accepted' || before === 'tentative' || before === 'declined';
  if (answered && before !== rsvp) return `${name} changed to ${ANSWER[rsvp]}`;
  return rsvp === 'accepted' ? `${name} is attending` : `${name} might attend`;
}

/** "Tue 21 Oct" for an occurrence key or local start ("2026-10-21T09:00" or "2026-10-21"). */
export function occurrenceLabel(local: string): string {
  return DateTime.fromISO(local, { zone: 'UTC' }).setLocale('en-GB').toFormat('ccc d LLL');
}

export interface EventEditChange { field: 'time' | 'location' | 'title' | 'description' | 'recurrence'; to?: string | null }

/** System line text for an organiser's edit. `occurrence` is set when only one date of a series changed. */
export function eventEditText(changes: EventEditChange[], t: { starts_local: string; ends_local: string; all_day: boolean }, occurrence?: string | null): string {
  const parts: string[] = [];
  for (const c of changes) {
    if (c.field === 'time') parts.push(`the time to ${formatEventWhen(t.starts_local, t.ends_local, t.all_day)}`);
    else if (c.field === 'location') parts.push(c.to ? `the location to ${c.to}` : 'the location (removed)');
    else if (c.field === 'title') parts.push(`the title to “${c.to}”`);
    else if (c.field === 'description') parts.push('the details');
    else if (c.field === 'recurrence') parts.push('how often it repeats');
  }
  if (!parts.length) return '';
  const list = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}`;
  return occurrence ? `changed ${list} for ${occurrenceLabel(occurrence)} only` : `changed ${list}`;
}

/**
 * t213u: the invite summary: "2 going · 1 maybe", leaving out answers nobody gave; "No replies yet" when nobody has
 * answered at all. Desktop and phone.
 */
export function rsvpSummary(attendees: Array<{ rsvp: Rsvp }>): string {
  const n = { accepted: 0, tentative: 0, declined: 0, pending: 0 } as Record<Rsvp, number>;
  for (const a of attendees) n[a.rsvp]++;
  const parts = ([['accepted', 'going'], ['tentative', 'maybe'], ['declined', 'not going'], ['pending', 'not replied']] as const)
    .filter(([k]) => n[k] > 0).map(([k, l]) => `${n[k]} ${l}`);
  if (!n.accepted && !n.tentative && !n.declined) return 'No replies yet';
  return parts.join(' · ');
}

/** t360u: one invitee as the attendee list draws them. `job` / `building` are the directory facts anyone may see. */
export interface RsvpPerson { user_id: string; display_name: string; rsvp: Rsvp; job?: string | null; building?: string | null }

export interface RsvpGroup<P extends RsvpPerson = RsvpPerson> {
  key: Rsvp;
  label: string;
  people: P[];
  /** Not replied for someone who may not see the names: how many, with no list. */
  hidden?: number;
}

const GROUP_LABEL: Record<Rsvp, string> = { accepted: 'Going', tentative: 'Maybe', declined: 'Can’t go', pending: 'Not replied' };

/**
 * t360u: who's coming, grouped Going, Maybe, Can't go, Not replied (empty groups left out), names A-Z. Everyone who can
 * see the event sees who answered; the names of those who haven't replied are for the organiser and the managers who
 * may edit it (`canSeePending`, the event's can_edit). Others get the Not replied count only.
 */
export function rsvpGroups<P extends RsvpPerson>(attendees: readonly P[], canSeePending: boolean): RsvpGroup<P>[] {
  const order: Rsvp[] = ['accepted', 'tentative', 'declined', 'pending'];
  const out: RsvpGroup<P>[] = [];
  for (const key of order) {
    const people = attendees.filter(a => a.rsvp === key).sort((a, b) => a.display_name.localeCompare(b.display_name, 'en-GB'));
    if (!people.length) continue;
    if (key === 'pending' && !canSeePending) out.push({ key, label: GROUP_LABEL[key], people: [], hidden: people.length });
    else out.push({ key, label: GROUP_LABEL[key], people });
  }
  return out;
}

/** "Picker · Northwich": the muted line under a name in the attendee list ('' when we know neither). */
export const rsvpPersonLine = (p: Pick<RsvpPerson, 'job' | 'building'>) => [p.job, p.building].filter(Boolean).join(' · ');

/** "3 people haven't replied" for the count-only Not replied group. */
export const rsvpHiddenText = (n: number) => `${n} ${n === 1 ? 'person hasn’t' : 'people haven’t'} replied`;
